import React, { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Fuel, 
  Users, 
  Target,
  MapPin,
  BarChart3,
  PieChart,
  Activity,
  Zap,
  AlertTriangle,
  CheckCircle,
  Play,
  Settings
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import PerformanceMetricsChart, { MetricData } from '../charts/PerformanceMetricsChart';
import PerformanceRangeChart from '../charts/PerformanceRangeChart';
import EnhancedPerformanceComparison from './EnhancedPerformanceComparison';
import ExpenseAnalysisDashboard from './ExpenseAnalysisDashboard';
import EnhancedCompetitorAnalysis from './EnhancedCompetitorAnalysis';

interface DemoVisualizationProps {
  showPerformanceDashboard?: boolean;
  showCompetitorMapping?: boolean;
}

export default function DemoVisualization({ 
  showPerformanceDashboard = true, 
  showCompetitorMapping = true 
}: DemoVisualizationProps) {
  const [activeTab, setActiveTab] = useState<'performance' | 'benchmarking' | 'expenses' | 'competitor'>('performance');
  const [selectedTimeframe, setSelectedTimeframe] = useState<'1M' | '3M' | '6M' | '1Y'>('3M');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showHeatMap, setShowHeatMap] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);

  // Mock performance data
  const performanceData = {
    currentValue: 2850000,
    previousValue: 2650000,
    monthlyRevenue: 485000, // Main monthly revenue
    monthlyGallons: 125000,
    fuelMargin: 0.18,
    storeRevenue: 95000, // Store portion of monthly revenue (about 20%)
    operatingExpenses: 125000,
    netIncome: 360000, // Updated to be more realistic (485k - 125k expenses)
    benchmarks: {
      industryAverage: {
        fuelMargin: 0.15,
        storeRevenue: 75000,
        gallonsPerMonth: 110000
      },
      topPerformers: {
        fuelMargin: 0.22,
        storeRevenue: 120000,
        gallonsPerMonth: 140000
      }
    },
    trends: {
      revenue: [420000, 435000, 450000, 465000, 485000], // Matches current monthly revenue
      gallons: [118000, 120000, 122000, 124000, 125000],
      margin: [0.16, 0.165, 0.17, 0.175, 0.18]
    }
  };

  // Mock competitor data
  const competitorData = {
    subjectProperty: {
      name: 'Shell Station - Main St',
      address: '123 Main St, Los Angeles, CA',
      fuelPrice: 4.25,
      monthlyVolume: 150000,
      marketShare: 0.28
    },
    competitors: [
      { name: 'Chevron', distance: 0.3, price: 4.32, volume: 135000, marketShare: 0.24 },
      { name: 'Arco', distance: 0.5, price: 4.18, volume: 120000, marketShare: 0.21 },
      { name: 'Mobil', distance: 0.8, price: 4.28, volume: 110000, marketShare: 0.19 },
      { name: 'Independent', distance: 1.2, price: 4.15, volume: 45000, marketShare: 0.08 }
    ],
    gameTheory: {
      nashEquilibrium: {
        optimalPrice: 4.23,
        expectedVolume: 155000,
        expectedRevenue: 655650,
        marketShare: 0.31
      },
      scenarios: [
        { name: '5¢ Price Decrease', volumeChange: +12000, revenueChange: +25000, roi: 0.15 },
        { name: 'Premium Service', volumeChange: +8000, revenueChange: +35000, roi: 0.22 },
        { name: 'Loyalty Program', volumeChange: +15000, revenueChange: +42000, roi: 0.28 }
      ]
    }
  };

  const handleRunAnalysis = async () => {
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    
    // Simulate analysis progress
    const progressSteps = [
      { progress: 20, delay: 500 },
      { progress: 45, delay: 800 },
      { progress: 70, delay: 600 },
      { progress: 90, delay: 400 },
      { progress: 100, delay: 300 }
    ];
    
    for (const step of progressSteps) {
      await new Promise(resolve => setTimeout(resolve, step.delay));
      setAnalysisProgress(step.progress);
      
      if (step.progress === 70) {
        setShowHeatMap(true); // Show heat map animation
      }
    }
    
    setIsAnalyzing(false);
    
    // Hide heat map after 3 seconds
    setTimeout(() => setShowHeatMap(false), 3000);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getChangeColor = (current: number, previous: number) => {
    return current > previous ? 'text-green-500' : 'text-red-500';
  };

  const getChangeIcon = (current: number, previous: number) => {
    return current > previous ? TrendingUp : TrendingDown;
  };

  const calculateChange = (current: number, previous: number) => {
    const change = ((current - previous) / previous) * 100;
    return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Fuel Central Analytics Demo
        </h1>
        <p className="text-gray-600">
          Experience comprehensive gas station performance analytics and competitive intelligence
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center mb-6">
        <div className="bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('performance')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'performance'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('benchmarking')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'benchmarking'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Benchmarking
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'expenses'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Expenses
          </button>
          <button
            onClick={() => setActiveTab('competitor')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'competitor'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Target className="w-4 h-4 inline mr-2" />
            Competitors
          </button>
        </div>
      </div>

      {/* Performance Dashboard */}
      {activeTab === 'performance' && showPerformanceDashboard && (
        <div className="space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Property Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(performanceData.currentValue)}
                  </p>
                  <div className={`flex items-center mt-1 ${getChangeColor(performanceData.currentValue, performanceData.previousValue)}`}>
                    {React.createElement(getChangeIcon(performanceData.currentValue, performanceData.previousValue), { className: "w-4 h-4 mr-1" })}
                    <span className="text-sm font-medium">
                      {calculateChange(performanceData.currentValue, performanceData.previousValue)}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(performanceData.monthlyRevenue)}
                  </p>
                  <div className="flex items-center mt-1 text-green-500">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">+8.2%</span>
                  </div>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Monthly Gallons</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(performanceData.monthlyGallons)}
                  </p>
                  <div className="flex items-center mt-1 text-green-500">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">+5.9%</span>
                  </div>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Fuel className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Fuel Margin</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(performanceData.fuelMargin * 100).toFixed(1)}%
                  </p>
                  <div className="flex items-center mt-1 text-green-500">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">+2.1%</span>
                  </div>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Activity className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </Card>
          </div>

         {/* Performance Charts */}
<div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
  <Card className="p-6 h-full flex flex-col">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">Performance Comparison</h3>
      <span className="text-xs text-gray-500">vs. Industry</span>
    </div>
    <PerformanceMetricsChart
      metrics={[
        {
          metricName: 'Fuel Margin',
          userValue: performanceData.fuelMargin * 100,
          average: performanceData.benchmarks.industryAverage.fuelMargin * 100,
          percentile25: 13,
          percentile75: 18,
          percentileRank: 72,
          desiredTrend: 'up',
          unit: '%',
          isMonetary: false
        },
        {
          metricName: 'Store Revenue',
          userValue: performanceData.storeRevenue,
          average: performanceData.benchmarks.industryAverage.storeRevenue,
          percentile25: 60000,
          percentile75: 100000,
          percentileRank: 65,
          desiredTrend: 'up',
          unit: '$',
          isMonetary: true
        },
        {
          metricName: 'Monthly Gallons',
          userValue: performanceData.monthlyGallons,
          average: performanceData.benchmarks.industryAverage.gallonsPerMonth,
          percentile25: 100000,
          percentile75: 135000,
          percentileRank: 68,
          desiredTrend: 'up',
          unit: 'gal',
          isMonetary: false
        }
      ]}
      chartType="radar"
      height={360}
    />
  </Card>

  <Card className="p-6 h-full flex flex-col">
    <h3 className="text-lg font-semibold mb-4">Performance vs Benchmarks</h3>
    <PerformanceRangeChart
      metrics={[
        {
          metricName: 'Fuel Margin',
          userValue: performanceData.fuelMargin * 100,
          average: performanceData.benchmarks.industryAverage.fuelMargin * 100,
          percentile25: 13,
          percentile75: 18,
          percentileRank: 72,
          desiredTrend: 'up',
          unit: '%',
          isMonetary: false
        },
        {
          metricName: 'Store Revenue',
          userValue: performanceData.storeRevenue,
          average: performanceData.benchmarks.industryAverage.storeRevenue,
          percentile25: 60000,
          percentile75: 100000,
          percentileRank: 65,
          desiredTrend: 'up',
          unit: '$',
          isMonetary: true
        },
        {
          metricName: 'Monthly Gallons',
          userValue: performanceData.monthlyGallons,
          average: performanceData.benchmarks.industryAverage.gallonsPerMonth,
          percentile25: 100000,
          percentile75: 135000,
          percentileRank: 68,
          desiredTrend: 'up',
          unit: 'gal',
          isMonetary: false
        }
      ]}
      industryBenchmarks={[
        { metricName: 'Fuel Margin', nationalAverage: 15, industryMin: 10, industryMax: 22, topPerformerThreshold: 20, unit: '%', desiredTrend: 'up', isMonetary: false, category: 'Operations' },
        { metricName: 'Store Revenue', nationalAverage: 75000, industryMin: 50000, industryMax: 120000, topPerformerThreshold: 110000, unit: '$', desiredTrend: 'up', isMonetary: true, category: 'Revenue' },
        { metricName: 'Monthly Gallons', nationalAverage: 110000, industryMin: 90000, industryMax: 150000, topPerformerThreshold: 130000, unit: 'gal', desiredTrend: 'up', isMonetary: false, category: 'Volume' }
      ]}
      height={360}
    />
  </Card>
</div>

          {/* Financial Summary */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(performanceData.netIncome)}
                </div>
                <div className="text-sm text-gray-600">Net Income (Monthly)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(performanceData.operatingExpenses)}
                </div>
                <div className="text-sm text-gray-600">Operating Expenses</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {((performanceData.netIncome / performanceData.monthlyRevenue) * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Profit Margin</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Enhanced Benchmarking Analysis */}
      {activeTab === 'benchmarking' && (
        <EnhancedPerformanceComparison data={performanceData} />
      )}

      {/* Expense Analysis Dashboard */}
      {activeTab === 'expenses' && (
        <ExpenseAnalysisDashboard data={performanceData} />
      )}

      {/* Enhanced Competitor Analysis */}
      {activeTab === 'competitor' && showCompetitorMapping && (
        <EnhancedCompetitorAnalysis data={performanceData} />
      )}

      {/* Legacy Competitor Mapping (Commented Out) */}
      {false && activeTab === 'competitor' && showCompetitorMapping && (
        <div className="space-y-6">
          {/* Map and Controls */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Competitive Landscape</h3>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600">Connected</span>
                  </div>
                </div>
                <div className="h-96 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg relative overflow-hidden">
                  {/* SVG Map */}
                  <svg className="w-full h-full" viewBox="0 0 600 400">
                    {/* Background */}
                    <rect width="600" height="400" fill="#f0f9ff" />
                    
                    {/* Streets */}
                    <g stroke="#d1d5db" strokeWidth="3" fill="none">
                      {/* Main Street (horizontal) */}
                      <line x1="0" y1="200" x2="600" y2="200" stroke="#9ca3af" strokeWidth="4" />
                      <text x="300" y="195" textAnchor="middle" fontSize="12" fill="#6b7280" fontWeight="bold">Main Street</text>
                      
                      {/* Oak Avenue (vertical) */}
                      <line x1="300" y1="0" x2="300" y2="400" stroke="#9ca3af" strokeWidth="4" />
                      <text x="305" y="100" fontSize="12" fill="#6b7280" fontWeight="bold">Oak Ave</text>
                      
                      {/* Secondary streets */}
                      <line x1="150" y1="0" x2="150" y2="400" stroke="#d1d5db" strokeWidth="2" />
                      <line x1="450" y1="0" x2="450" y2="400" stroke="#d1d5db" strokeWidth="2" />
                      <line x1="0" y1="100" x2="600" y2="100" stroke="#d1d5db" strokeWidth="2" />
                      <line x1="0" y1="300" x2="600" y2="300" stroke="#d1d5db" strokeWidth="2" />
                    </g>
                    
                    {/* Buildings/Blocks */}
                    <g fill="#e5e7eb" stroke="#d1d5db" strokeWidth="1">
                      <rect x="20" y="20" width="110" height="60" rx="4" />
                      <rect x="170" y="20" width="110" height="60" rx="4" />
                      <rect x="320" y="20" width="110" height="60" rx="4" />
                      <rect x="470" y="20" width="110" height="60" rx="4" />
                      
                      <rect x="20" y="120" width="110" height="60" rx="4" />
                      <rect x="320" y="120" width="110" height="60" rx="4" />
                      <rect x="470" y="120" width="110" height="60" rx="4" />
                      
                      <rect x="20" y="220" width="110" height="60" rx="4" />
                      <rect x="170" y="220" width="110" height="60" rx="4" />
                      <rect x="470" y="220" width="110" height="60" rx="4" />
                      
                      <rect x="20" y="320" width="110" height="60" rx="4" />
                      <rect x="170" y="320" width="110" height="60" rx="4" />
                      <rect x="320" y="320" width="110" height="60" rx="4" />
                      <rect x="470" y="320" width="110" height="60" rx="4" />
                    </g>
                    
                    {/* Gas Station Markers */}
                    
                    {/* Subject Property - Shell Station (center) */}
                    <g>
                      <circle cx="300" cy="200" r="12" fill="#8b5cf6" stroke="#ffffff" strokeWidth="3" />
                      <circle cx="300" cy="200" r="6" fill="#ffffff" />
                      <text x="300" y="230" textAnchor="middle" fontSize="11" fill="#8b5cf6" fontWeight="bold">Shell Station</text>
                      <text x="300" y="242" textAnchor="middle" fontSize="9" fill="#6b7280">Subject Property</text>
                    </g>
                    
                    {/* Competitor 1 - Chevron (top-left) */}
                    <g>
                      <circle cx="180" cy="120" r="10" fill="#ef4444" stroke="#ffffff" strokeWidth="2" />
                      <circle cx="180" cy="120" r="4" fill="#ffffff" />
                      <text x="180" y="140" textAnchor="middle" fontSize="10" fill="#ef4444" fontWeight="bold">Chevron</text>
                      <text x="180" y="150" textAnchor="middle" fontSize="8" fill="#6b7280">0.3 mi</text>
                    </g>
                    
                    {/* Competitor 2 - Arco (top-right) */}
                    <g>
                      <circle cx="420" cy="140" r="10" fill="#3b82f6" stroke="#ffffff" strokeWidth="2" />
                      <circle cx="420" cy="140" r="4" fill="#ffffff" />
                      <text x="420" y="160" textAnchor="middle" fontSize="10" fill="#3b82f6" fontWeight="bold">Arco</text>
                      <text x="420" y="170" textAnchor="middle" fontSize="8" fill="#6b7280">0.5 mi</text>
                    </g>
                    
                    {/* Competitor 3 - Mobil (bottom-left) */}
                    <g>
                      <circle cx="120" cy="280" r="10" fill="#10b981" stroke="#ffffff" strokeWidth="2" />
                      <circle cx="120" cy="280" r="4" fill="#ffffff" />
                      <text x="120" y="300" textAnchor="middle" fontSize="10" fill="#10b981" fontWeight="bold">Mobil</text>
                      <text x="120" y="310" textAnchor="middle" fontSize="8" fill="#6b7280">0.8 mi</text>
                    </g>
                    
                    {/* Competitor 4 - Independent (bottom-right) */}
                    <g>
                      <circle cx="480" cy="320" r="10" fill="#f59e0b" stroke="#ffffff" strokeWidth="2" />
                      <circle cx="480" cy="320" r="4" fill="#ffffff" />
                      <text x="480" y="340" textAnchor="middle" fontSize="10" fill="#f59e0b" fontWeight="bold">Independent</text>
                      <text x="480" y="350" textAnchor="middle" fontSize="8" fill="#6b7280">1.2 mi</text>
                    </g>
                    
                    {/* Analysis radius circle */}
                    <circle cx="300" cy="200" r="80" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
                    
                    {/* Legend */}
                    <g transform="translate(20, 20)">
                      <rect x="0" y="0" width="140" height="80" fill="rgba(255,255,255,0.9)" stroke="#d1d5db" rx="4" />
                      <text x="8" y="15" fontSize="12" fill="#374151" fontWeight="bold">Map Legend</text>
                      
                      <circle cx="15" cy="30" r="6" fill="#8b5cf6" />
                      <text x="25" y="34" fontSize="10" fill="#374151">Subject Property</text>
                      
                      <circle cx="15" cy="45" r="5" fill="#ef4444" />
                      <text x="25" y="49" fontSize="10" fill="#374151">Competitors</text>
                      
                      <line x1="8" y1="60" x2="18" y2="60" stroke="#8b5cf6" strokeDasharray="2,2" />
                      <text x="25" y="64" fontSize="10" fill="#374151">Analysis Radius</text>
                    </g>
                    
                    {/* Scale */}
                    <g transform="translate(480, 360)">
                      <rect x="0" y="0" width="100" height="30" fill="rgba(255,255,255,0.9)" stroke="#d1d5db" rx="2" />
                      <line x1="10" y1="20" x2="50" y2="20" stroke="#374151" strokeWidth="2" />
                      <line x1="10" y1="15" x2="10" y2="25" stroke="#374151" strokeWidth="1" />
                      <line x1="50" y1="15" x2="50" y2="25" stroke="#374151" strokeWidth="1" />
                      <text x="30" y="14" textAnchor="middle" fontSize="9" fill="#374151">1 mile</text>
                    </g>
                  
                  
                  {/* Animated Heat Map Overlay */}
{showHeatMap && (
  <g className="animate-pulse">
    {/* Heat zones with gradient animation */}
    <defs>
      <radialGradient id="heatGradient1" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="rgba(239, 68, 68, 0.6)" />
        <stop offset="100%" stopColor="rgba(239, 68, 68, 0.1)" />
      </radialGradient>
      <radialGradient id="heatGradient2" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="rgba(59, 130, 246, 0.6)" />
        <stop offset="100%" stopColor="rgba(59, 130, 246, 0.1)" />
      </radialGradient>
      <radialGradient id="heatGradient3" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="rgba(16, 185, 129, 0.6)" />
        <stop offset="100%" stopColor="rgba(16, 185, 129, 0.1)" />
      </radialGradient>
    </defs>
    
    {/* Animated heat circles */}
    <circle cx="180" cy="120" r="60" fill="url(#heatGradient1)" className="animate-ping" />
    <circle cx="420" cy="140" r="50" fill="url(#heatGradient2)" className="animate-ping" style={{animationDelay: '0.2s'}} />
    <circle cx="120" cy="280" r="45" fill="url(#heatGradient3)" className="animate-ping" style={{animationDelay: '0.4s'}} />
    <circle cx="300" cy="200" r="70" fill="rgba(139, 92, 246, 0.3)" className="animate-pulse" />
    
    {/* Market influence lines */}
    <g stroke="rgba(139, 92, 246, 0.5)" strokeWidth="2" strokeDasharray="4,4" className="animate-pulse">
      <line x1="300" y1="200" x2="180" y2="120" />
      <line x1="300" y1="200" x2="420" y2="140" />
      <line x1="300" y1="200" x2="120" y2="280" />
    </g>
    </g>
)}
                  </svg>

                  {/* Interactive overlay info */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                    <div className="flex items-center gap-2 text-green-600 mb-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Live Data</span>
                    </div>
                    <p className="text-xs text-gray-600">5 mile radius analysis</p>
                    <p className="text-xs text-gray-600">4 competitors detected</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-4">
            <Card className="p-4">
                <h4 className="font-semibold mb-3">Analysis Controls</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Analysis Radius
                    </label>
                    <select className="w-full p-2 border border-gray-300 rounded-md">
                      <option>2 miles</option>
                      <option selected>5 miles</option>
                      <option>10 miles</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Game Theory Model
                    </label>
                    <select className="w-full p-2 border border-gray-300 rounded-md">
                      <option selected>Bertrand Competition</option>
                      <option>Cournot Competition</option>
                      <option>Stackelberg Leadership</option>
                    </select>
                  </div>
                  <Button 
                    className={`w-full transition-all duration-300 ${
                      isAnalyzing ? 'bg-orange-500 hover:bg-orange-600' : ''
                    }`}
                    onClick={handleRunAnalysis}
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <div className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                        Analyzing... {analysisProgress}%
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Run Analysis
                      </>
                    )}
                  </Button>
                </div>
              </Card>

              <Card className="p-4">
                <h4 className="font-semibold mb-3">Market Position</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Price:</span>
                    <span className="font-medium">${competitorData.subjectProperty.fuelPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Market Share:</span>
                    <span className="font-medium">{(competitorData.subjectProperty.marketShare * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Volume:</span>
                    <span className="font-medium">{formatNumber(competitorData.subjectProperty.monthlyVolume)}</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Competitor Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Nearby Competitors</h3>
              <div className="space-y-3">
                {competitorData.competitors.map((competitor, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">{competitor.name}</div>
                      <div className="text-sm text-gray-600">{competitor.distance} miles away</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">${competitor.price.toFixed(2)}</div>
                      <div className="text-sm text-gray-600">{(competitor.marketShare * 100).toFixed(1)}% share</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Game Theory Results</h3>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Nash Equilibrium</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700">Optimal Price:</span>
                      <span className="font-medium">${competitorData.gameTheory.nashEquilibrium.optimalPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Expected Volume:</span>
                      <span className="font-medium">{formatNumber(competitorData.gameTheory.nashEquilibrium.expectedVolume)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Market Share:</span>
                      <span className="font-medium">{(competitorData.gameTheory.nashEquilibrium.marketShare * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Scenario Analysis</h4>
                  <div className="space-y-2">
                    {competitorData.gameTheory.scenarios.map((scenario, index) => (
                      <div key={index} className="p-3 border rounded-lg">
                        <div className="font-medium text-sm">{scenario.name}</div>
                        <div className="flex justify-between text-xs text-gray-600 mt-1">
                          <span>Volume: +{formatNumber(scenario.volumeChange)}</span>
                          <span>Revenue: +{formatCurrency(scenario.revenueChange)}</span>
                          <span className="text-green-600">ROI: {(scenario.roi * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Simulation Controls */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Competitive Simulation</h3>
              <Button>
                <Settings className="w-4 h-4 mr-2" />
                Configure Scenario
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">+12K</div>
                <div className="text-sm text-gray-600">Volume Increase</div>
                <div className="text-xs text-gray-500">5¢ price reduction</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">+$25K</div>
                <div className="text-sm text-gray-600">Revenue Impact</div>
                <div className="text-xs text-gray-500">Monthly projection</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600">15%</div>
                <div className="text-sm text-gray-600">ROI</div>
                <div className="text-xs text-gray-500">12-month horizon</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Footer */}
      <div className="text-center py-8 border-t">
        <p className="text-gray-600">
          This demonstration showcases the comprehensive analytics capabilities of Fuel Central
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Real-time data integration • Advanced game theory modeling • Competitive intelligence
        </p>
      </div>
    </div>
  );
}
